import React from 'react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const AnnualReview = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="Annual Medicare Review"
        description="Annual Medicare plan review and optimization services. Ensure your Medicare coverage still meets your needs during Annual Enrollment Period (AEP). Expert assistance to compare plans and save money."
        keywords="Annual Medicare Review, Annual Enrollment Period, AEP, Medicare plan review, Medicare optimization, change Medicare plans, Medicare savings"
        path="/annual-review"
      />
      {/* Top Bar - Light Blue-Grey */}
      <section className="py-2 px-4 bg-gray-200">
        <div className="container mx-auto max-w-7xl text-right">
          <p className="text-gray-700 text-sm font-semibold">
            Annual Enrollment Period (AEP): October 15 - December 7
          </p>
        </div>
      </section>

      {/* Red Banner */}
      <section className="py-3 px-4 bg-red-600">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-lg font-bold text-white">
            Did you know? Medicare recommends all Advantage Plan members get an annual review.
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center mb-8">
            <div className="lg:col-span-3">
              <h1 className="text-4xl font-bold mb-2 text-gray-900">Plans change.</h1>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 text-gray-900">Get an Annual Review.</h1>
              <p className="text-lg text-gray-800 leading-relaxed">
                Every year, plans can change. Premiums may be added. Benefits may be reduced. Copays, drug coverage, provider networks, dental, vision, hearing, and extra benefits can all shift from one year to the next.
              </p>
            </div>
            <div className="lg:col-span-2">
              <img 
                src="/images/annual-review.png"
                alt="Senior couple reviewing Medicare plans"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>

          <div className="text-center mb-12">
            <Button asChild size="lg" className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white text-xl px-12 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                Take the 2-Minute Medicare Checkup
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Don't Overpay Section */}
      <section className="py-8 px-4 bg-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-2">Don't overpay. Don't wait.</h2>
          <p className="text-xl text-gray-800">Get your Annual Review.</p>
        </div>
      </section>

      {/* Important Message */}
      <section className="py-12 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm font-bold text-red-600 uppercase mb-3">IMPORTANT MESSAGE</p>
          <h3 className="text-3xl font-bold text-gray-900 mb-4">Medicare plans change every year.</h3>
          <p className="text-lg text-gray-800">Benefits can be lost. Costs can increase. Your medications may no longer be covered.</p>
        </div>
      </section>

      {/* What Should Be Reviewed */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-900">What should be reviewed each year?</h2>
          <p className="text-xl text-center mb-12 text-gray-700 italic">
            A proper Medicare review is not just checking the premium. It means looking at the whole picture—because a pretty brochure can hide an ugly copay.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              'Monthly premium',
              'Primary care and specialist copays',
              'Hospital costs',
              'Prescription drug coverage',
              'Doctor and hospital network access',
              'Maximum out-of-pocket limit',
              'Dental, vision, and hearing benefits',
              'Over-the-counter, food, utility, or transportation allowances',
              'Prior authorization requirements'
            ].map((item, index) => (
              <div key={index} className="bg-gray-100 border border-gray-200 rounded-lg p-4">
                <p className="text-base text-gray-900">{item}</p>
              </div>
            ))}
            <div className="md:col-span-2 bg-gray-100 border border-gray-200 rounded-lg p-4">
              <p className="text-base text-gray-900">Coverage for your current medications and conditions</p>
            </div>
          </div>

          <div className="text-center">
            <Button asChild size="lg" className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white text-lg md:text-xl px-8 md:px-12 py-6">
              <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer" className="block">
                <span className="block">Annual Enrollment Period</span>
                <span className="block">October 15 through December 7</span>
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">An Annual Review Is as Simple as 1, 2, 3</h2>
          
          <div className="space-y-6 mb-12">
            {/* Step 1 */}
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="text-5xl font-bold text-teal-600">1</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Take the Survey</h3>
                  <p className="text-base text-gray-800 mb-6">Share your current plan, medications, and priorities so we understand your needs.</p>
                  <Button asChild className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white text-lg px-8 py-4">
                    <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
                      Start Survey
                    </a>
                  </Button>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="flex items-start gap-4">
                <div className="text-5xl font-bold text-teal-600">2</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">We Research Your Options</h3>
                  <p className="text-base text-gray-800">We compare plans, costs, and coverage to find the best options for your situation.</p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-gray-100 rounded-lg p-8">
              <div className="flex items-start gap-4 mb-4">
                <div className="text-5xl font-bold text-teal-600">3</div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">You Get Clear Answers</h3>
                  <p className="text-base text-gray-800 mb-6">We walk you through your options so you know exactly what works best for you.</p>
                  <Button asChild className="bg-gradient-to-r from-teal-600 to-green-600 hover:from-teal-700 hover:to-green-700 text-white text-lg px-8 py-4">
                    <a href="https://calendly.com/llplans/30min" target="_blank" rel="noopener noreferrer">
                      Schedule a Call
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="py-16 px-4 bg-gradient-to-r from-teal-600 to-green-600">
        <div className="container mx-auto max-w-4xl text-center">
          <Button asChild size="lg" className="bg-white text-teal-600 hover:bg-gray-100 text-2xl px-16 py-8 mb-4">
            <a href="https://app.retireflo.com/paulette-lifeline-plans" target="_blank" rel="noopener noreferrer">
              Start My Free Annual Review
            </a>
          </Button>
          <p className="text-lg text-white mt-4">Takes 5 minutes. No obligation. 100% free</p>
        </div>
      </section>
    </div>
  );
};

export default AnnualReview;
